import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, useWindowDimensions } from 'react-native';
import Animated, { AnimatedStyle } from 'react-native-reanimated';

import { EventInfoWithoutPlayers } from '@/api/events/types/types';
import PlayIcon from '@/assets/icons/play';
import { ImageWithGradient } from '@/components/ImageWithGradient';
import { Text } from '@/components/TextComponent';
import { Row } from '@/components/lib/components';
import { Box } from '@/components/lib/components/Box';
import { ds } from '@/components/lib/design.system';
import { AppColors } from '@/components/lib/theme';
import { LinearGradient } from '@/components/linear-gradient/LinearGradient';
import { common, designSystem, withOpacity } from '@/styles/styles';
import { EventAttributesType, EventStatus } from '@/types/api.generated';
import { isWeb } from '@/utils/constants-platform-specific';
import { findEventAttribute } from '@/utils/fantasy-attribute-utils';
import { gameUtils } from '@/utils/games';
import { isPGA, isUFC } from '@/utils/league';

export const EVENT_HEADER_HERO_IMAGE_HEIGHT = 296;

type Props = {
    singleEvent: EventInfoWithoutPlayers | null;
    headerOverlayStyle: AnimatedStyle;
};

const BackgroundContainer = ({ image, children }: { image?: string; children: ReactNode }) => {
    const { width } = useWindowDimensions();
    const gradientColors = isWeb
        ? [withOpacity(designSystem.colors.neutralBlack, 0), designSystem.colors.neutralBlack]
        : undefined;
    return (
        <Box zIndex={0}>
            {image ? (
                <>
                    <ImageWithGradient source={{ uri: image }} gradientColors={gradientColors} />
                    {children}
                </>
            ) : (
                <>
                    <LinearGradient
                        colors={[ds.colorValue.background.danger.strong, '#000000']}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 0, y: 1 }}
                        width={width}
                        height={EVENT_HEADER_HERO_IMAGE_HEIGHT}
                    />
                    {children}
                </>
            )}
        </Box>
    );
};

export const EventHeader = React.memo(({ singleEvent, headerOverlayStyle }: Props) => {
    const { t } = useTranslation('sports_events');

    if (!singleEvent) {
        return null;
    }

    const roundNumber = findEventAttribute(singleEvent, EventAttributesType.RoundNumber) ?? '';
    const startDate = findEventAttribute(singleEvent, EventAttributesType.TournamentStartDateTime) ?? '';
    const endDate = findEventAttribute(singleEvent, EventAttributesType.TournamentEndDateTime) ?? '';
    const title = gameUtils.createHeaderTitleLabel(singleEvent);

    const isLive = isPGA(singleEvent.league)
        ? singleEvent.status === EventStatus.InProgress
        : new Date() > new Date(startDate);

    const date = isUFC(singleEvent.league)
        ? gameUtils.createTournamentTimeLabel(singleEvent)
        : gameUtils.createTimeLabel(singleEvent);
    const dateRanges = gameUtils.formatTournamentDates(startDate, endDate);
    const info = gameUtils.createInfoLabel(singleEvent);
    const image = singleEvent.headerImage;

    const headerOverlayZindex = isWeb ? -1 : 4;

    return (
        <BackgroundContainer image={image}>
            <Box paddingHorizontal={'s16'} zIndex={2}>
                <Box height={EVENT_HEADER_HERO_IMAGE_HEIGHT} justifyContent={'flex-end'} paddingBottom={'s12'}>
                    <Row gap={'s6'} paddingBottom={'s8'}>
                        {roundNumber ? (
                            <Tag
                                label={`${t('round')} ${roundNumber}`}
                                color={ds.colorToken.background.neutral.stronger}
                            />
                        ) : null}
                        {isLive ? (
                            <Tag
                                label="Live"
                                color={ds.colorToken.text.danger}
                                icon={<PlayIcon />}
                                labelColor="neutral900"
                            />
                        ) : null}
                    </Row>
                    <Text variant={'headlineMedium'} lineHeight={28} paddingBottom={'s4'}>
                        {title}
                    </Text>
                    <Text variant={'bodySmall'} color={ds.colorToken.text.secondary}>
                        {info}
                    </Text>
                    <Text variant={'bodySmall'} color={ds.colorToken.text.secondary}>
                        {date !== '' ? date : dateRanges}
                    </Text>
                </Box>
            </Box>
            <Animated.View
                style={[
                    StyleSheet.absoluteFill,
                    common.absolute,
                    { backgroundColor: ds.colorValue.background.neutral.base, zIndex: headerOverlayZindex },
                    headerOverlayStyle,
                ]}
            />
        </BackgroundContainer>
    );
});

export const Tag = ({
    icon,
    labelColor,
    color,
    label,
}: {
    icon?: React.JSX.Element;
    color: AppColors;
    labelColor?: AppColors;
    label: string;
}) => {
    return (
        <Row
            backgroundColor={color}
            paddingHorizontal={'s6'}
            paddingVertical={'s2'}
            style={styles.roundLabel}
            alignSelf={'flex-start'}
            borderRadius={'r6'}
            alignItems={'center'}
            gap="s2"
            height={20}
        >
            {icon}
            <Text variant="labelMedium" textAlign={'center'} fontWeight={'600'} color={labelColor}>
                {label}
            </Text>
        </Row>
    );
};

const styles = StyleSheet.create({
    roundLabel: {
        paddingBottom: 2.5,
        paddingTop: 1.5,
        borderCurve: 'continuous',
    },
});
